import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Socket from '../utils/Socket.config';
import { kinposPaymentBridge } from '../../utils/KinposBridge';
import { registerRemoteKinposVoidHandler } from '../../utils/KinposVoidResultRouter';
import {
   listenForRemoteKinposVoidRequests,
   listenForRemoteKinposVoidConfirmation,
   respondRemoteKinposVoid,
   tagContextForRemoteRelay,
} from '../../utils/KinposRemoteVoidRelay';

// KinPOS/PAX solo puede anular una venta desde el mismo dispositivo Android que la cobro. Este
// componente no tiene UI: se monta UNA sola vez por sesion de POS, en App.js (fuera de <Routes>,
// no dentro de una pantalla especifica) para convertir a ESTE punto en ejecutor de anulaciones
// remotas cuando otro punto pide devolver una orden que se cobro aqui - sin importar en que
// pantalla del POS este el operador. Montarlo dentro de una pantalla puntual (ej. PaymentPost.jsx)
// lo desconecta apenas el operador navega a otra ruta (Mesas, reportes, etc.), porque las rutas de
// este router son mutuamente excluyentes: la pantalla anterior se desmonta por completo.
//
// Si el punto que inicio la devolucion (B) nunca confirma el registro de la nota de credito (por
// ejemplo, se quedo sin conexion despues de recibir el resultado), este punto (A) no debe quedar
// bloqueado para siempre esperando un ACK que nunca llega: despues de CONFIRM_TIMEOUT_MS sin
// confirmacion, se trata como fallo de registro y el pending queda en el mismo estado recuperable
// ("registration_failed") que ya usa el resto del sistema para casos de backend no confirmado.
const CONFIRM_TIMEOUT_MS = 60000;

function KinposRemoteVoidExecutor() {
   const socketRef = useRef(null);
   const cleanupRef = useRef(null);
   // Identidad (maquina|sucursal|negocio) con la que se abrio el socket actual. Cerrar sesion en el POS
   // hace localStorage.clear() + navigate sin recargar la pagina, asi que sin comparar esto el socket
   // seguiria atado a la maquina anterior: un punto ya liberado seguiria ejecutando anulaciones remotas
   // y un login nuevo (otra maquina/sucursal) no tomaria efecto hasta recargar.
   const identityRef = useRef(null);
   const pendingConfirmTimeouts = useRef(new Map());
   // Al montarse en App.js, este componente existe ANTES de que el operador inicie sesion (cuando
   // localStorage.machine/business/branchoffice todavia no existen) - reintenta la conexion en cada
   // cambio de ruta (login siempre navega a alguna pantalla al terminar) en vez de solo al montar,
   // pero sin reconectar si ya hay una conexion viva CON LA MISMA identidad (ver abajo).
   const location = useLocation();

   const readSession = () => {
      try {
         const machine = JSON.parse(localStorage.getItem('machine'));
         const branchoffice = JSON.parse(localStorage.getItem('branchoffice'));
         const business = JSON.parse(localStorage.getItem('business'));
         if (!machine?.code || !branchoffice?.code || !business?.code) return null;
         return { machine, branchoffice, business, identity: `${machine.code}|${branchoffice.code}|${business.code}` };
      } catch (err) {
         return null;
      }
   };

   useEffect(() => {
      const session = readSession();

      // Misma sesion y ya conectado: nada que hacer.
      if (socketRef.current && session?.identity === identityRef.current) return undefined;
      // Sesion cerrada o distinta: se suelta la conexion anterior antes de abrir otra (o de no abrir ninguna).
      if (socketRef.current) cleanupRef.current?.();
      if (!session) return undefined;

      const { machine, branchoffice, business } = session;

      const socket = Socket({
         machine_code: machine.code,
         branchoffice_code: branchoffice.code,
         business_code: business.code,
      });
      socketRef.current = socket;
      identityRef.current = session.identity;

      const clearConfirmTimeout = (kinposRequestId) => {
         const timeoutId = pendingConfirmTimeouts.current.get(kinposRequestId);
         if (timeoutId) {
            clearTimeout(timeoutId);
            pendingConfirmTimeouts.current.delete(kinposRequestId);
         }
      };

      const respondError = (payload, code, message) => {
         respondRemoteKinposVoid(socket, {
            remoteRequestId: payload.remote_request_id,
            kinposRequestId: null,
            requestingMachineCode: payload.requesting_machine_code,
            businessCode: payload.business_code,
            branchofficeCode: payload.branchoffice_code,
            result: { status: 'error', code, message, request_id: null },
         });
      };

      const handleRemoteVoidRequest = async (payload) => {
         if (!payload?.remote_request_id || !payload?.void?.orderReference || !payload?.void?.receiptNumber) return;

         if (kinposPaymentBridge.hasPendingPayment()) {
            respondError(
               payload,
               'KINPOS_DEVICE_BUSY',
               'El terminal KinPOS/PAX de este punto ya tiene una operacion en proceso.'
            );
            return;
         }

         const taggedContext = tagContextForRemoteRelay(payload.context || {}, {
            remoteRequestId: payload.remote_request_id,
            requestingMachineCode: payload.requesting_machine_code,
            businessCode: payload.business_code,
            branchofficeCode: payload.branchoffice_code,
         });

         try {
            await kinposPaymentBridge.voidTransaction({
               payment: payload.payment,
               orderReference: payload.void.orderReference,
               receiptNumber: payload.void.receiptNumber,
               context: taggedContext,
            });
         } catch (error) {
            respondError(
               payload,
               error?.code || 'KINPOS_REMOTE_VOID_ERROR',
               error?.message || 'No se pudo iniciar la anulacion remota en este punto.'
            );
         }
      };

      const handleTerminalVoidResult = (result) => {
         if (!result || result.status === 'pending') return;
         const relay = result.request?.context?.remote_relay;
         if (!relay) return;

         if (result.status === 'uncertain') {
            // KinposPaymentBridge sigue reintentando/recuperando localmente; todavia no hay nada
            // terminal que reportar a B.
            return;
         }

         respondRemoteKinposVoid(socket, {
            remoteRequestId: relay.remote_request_id,
            kinposRequestId: result.request_id,
            requestingMachineCode: relay.requesting_machine_code,
            businessCode: relay.business_code,
            branchofficeCode: relay.branchoffice_code,
            result,
         });

         clearConfirmTimeout(result.request_id);
         pendingConfirmTimeouts.current.set(
            result.request_id,
            setTimeout(() => {
               pendingConfirmTimeouts.current.delete(result.request_id);
               kinposPaymentBridge.markProcessingFailed(result.request_id);
            }, CONFIRM_TIMEOUT_MS)
         );
      };

      const handleRemoteVoidConfirmation = (payload) => {
         if (!payload?.kinpos_request_id) return;
         clearConfirmTimeout(payload.kinpos_request_id);
         if (payload.success) {
            kinposPaymentBridge.complete(payload.kinpos_request_id);
         } else {
            kinposPaymentBridge.markProcessingFailed(payload.kinpos_request_id);
         }
      };

      const unsubscribeRequests = listenForRemoteKinposVoidRequests(socket, handleRemoteVoidRequest);
      const unsubscribeConfirmations = listenForRemoteKinposVoidConfirmation(socket, handleRemoteVoidConfirmation);
      const unregisterRemoteHandler = registerRemoteKinposVoidHandler(handleTerminalVoidResult);

      // No se registra como cleanup de ESTE efecto: como corre en cada cambio de ruta (ver deps),
      // devolver esto aqui lo ejecutaria en la SIGUIENTE navegacion, cerrando la conexion que recien
      // se abrio antes de que el guard de "ya conectado" de arriba llegara a verla. Se guarda para
      // desconectar solo cuando cambia la identidad de la sesion (ver arriba) o el componente se
      // desmonta de verdad (la app entera se cierra).
      cleanupRef.current = () => {
         unsubscribeRequests();
         unsubscribeConfirmations();
         unregisterRemoteHandler();
         pendingConfirmTimeouts.current.forEach((timeoutId) => clearTimeout(timeoutId));
         pendingConfirmTimeouts.current.clear();
         socket.disconnect();
         socketRef.current = null;
         identityRef.current = null;
      };
      return undefined;
   }, [location.pathname]);

   useEffect(() => () => cleanupRef.current?.(), []);

   return null;
}

export default KinposRemoteVoidExecutor;
